import type { TerpeneResult } from "../types";

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const element = document.createElement("textarea");
  element.value = value;
  element.setAttribute("readonly", "");
  element.style.position = "fixed";
  element.style.opacity = "0";
  document.body.append(element);
  element.select();
  document.execCommand("copy");
  element.remove();
}

export async function copyReferralLink(referralUrl: string): Promise<void> {
  await copyText(referralUrl);
}

async function cardPng(result: TerpeneResult): Promise<Blob> {
  const response = await fetch(result.shareCard);
  if (!response.ok) throw new Error("The result card could not be loaded.");
  const source = await response.blob();
  if (!source.type.includes("svg")) return source;

  const svgUrl = URL.createObjectURL(source);
  try {
    const image = new Image();
    image.src = svgUrl;
    await image.decode();
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("The result card could not be prepared.");
    context.drawImage(image, 0, 0);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("The result card could not be prepared.")), "image/png");
    });
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

export async function downloadShareCard(result: TerpeneResult): Promise<void> {
  const card = await cardPng(result);
  const url = URL.createObjectURL(card);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `root-to-rise-${result.id}.png`;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function shareResult(result: TerpeneResult, referralUrl: string): Promise<"shared" | "copied"> {
  const shareData: ShareData = {
    title: `I’m ${result.name} — ${result.title}`,
    text: `I discovered my Root to Rise terpene personality. Which one are you?`,
    url: referralUrl,
  };

  if (navigator.share) {
    try {
      const blob = await cardPng(result);
      const file = new File([blob], `root-to-rise-${result.id}.png`, { type: "image/png" });
      if (!navigator.canShare || navigator.canShare({ files: [file] })) {
        await navigator.share({ ...shareData, files: [file] });
      } else {
        await navigator.share(shareData);
      }
      return "shared";
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return "shared";
    }
  }

  await copyText(referralUrl);
  return "copied";
}
